import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { Box, ClickAwayListener, IconButton, Paper, Popper, Stack, Typography } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import Position from "../models/Position";
import { BillingPolicy } from "../types/BillingPolicy";
import OpsDateUtils from "../utils/OpsDateUtils";

interface Props {
  position?: Position;
}

const formatBoolean = (value: boolean) => (value ? "Yes" : "No");

const formatRoleKey = (value: string) =>
  value
    .toLowerCase()
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

const formatDate = (value?: Date) => (value ? OpsDateUtils.formatUTCAsDDMMMYYY(value) : "—");

const formatBillingPolicy = (value: BillingPolicy) => BillingPolicy[value] || value;

export default function PositionDetailsInfoButton({ position }: Props) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [isPinned, setIsPinned] = useState(false);
  const closeTimeoutRef = useRef<number | undefined>(undefined);

  const isOpen = Boolean(anchorEl) && !!position;

  const clearCloseTimeout = () => {
    if (closeTimeoutRef.current) {
      window.clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = undefined;
    }
  };

  const handleClose = () => {
    clearCloseTimeout();
    setAnchorEl(null);
    setIsPinned(false);
  };

  const scheduleClose = () => {
    clearCloseTimeout();

    if (isPinned) {
      return;
    }

    closeTimeoutRef.current = window.setTimeout(() => {
      setAnchorEl(null);
    }, 120);
  };

  const handleOpen = (target: HTMLElement, pinned = false) => {
    clearCloseTimeout();
    setAnchorEl(target);
    setIsPinned(pinned);
  };

  useEffect(() => {
    if (!position) {
      handleClose();
    }
  }, [position]);

  useEffect(() => {
    return () => clearCloseTimeout();
  }, []);

  return (
    <ClickAwayListener onClickAway={handleClose}>
      <Box sx={{ display: "flex", alignItems: "center", alignSelf: "center", mt: 1 }}>
        <IconButton
          aria-label="Position details"
          size="small"
          disabled={!position}
          onMouseEnter={(event) => position && handleOpen(event.currentTarget)}
          onMouseLeave={scheduleClose}
          onClick={(event) => {
            if (!position) {
              return;
            }

            if (isOpen && isPinned) {
              handleClose();
              return;
            }

            handleOpen(event.currentTarget, true);
          }}
          sx={{
            color: position ? "primary.main" : "action.disabled",
          }}
        >
          <InfoOutlinedIcon fontSize="small" />
        </IconButton>

        <Popper open={isOpen} anchorEl={anchorEl} placement="right-start" disablePortal sx={{ zIndex: 1500 }}>
          <Paper
            elevation={4}
            onMouseEnter={clearCloseTimeout}
            onMouseLeave={scheduleClose}
            sx={{ maxWidth: 320, p: 2, ml: 1, borderRadius: 2 }}
          >
            <Stack spacing={1}>
              <Typography variant="subtitle1" fontWeight={700}>
                Position details
              </Typography>

              <Box>
                <Typography variant="body2">
                  <strong>Internal hours:</strong> {position?.internalHours ?? "—"}
                </Typography>
                <Typography variant="body2">
                  <strong>External hours:</strong> {position?.externalHours ?? "—"}
                </Typography>
                <Typography variant="body2">
                  <strong>Role key:</strong> {position?.roleKey ? formatRoleKey(position.roleKey) : "—"}
                </Typography>
                <Typography variant="body2">
                  <strong>Billing policy:</strong> {position?.billingPolicy ? formatBillingPolicy(position.billingPolicy) : "—"}
                </Typography>
                <Typography variant="body2">
                  <strong>Start date:</strong> {formatDate(position?.shortStartDate)}
                </Typography>
                <Typography variant="body2">
                  <strong>End date:</strong> {formatDate(position?.shortEndDate)}
                </Typography>
                <Typography variant="body2">
                  <strong>Bonus:</strong> {typeof position?.bonus === "number" ? `${position.bonus}%` : "—"}
                </Typography>
                <Typography variant="body2">
                  <strong>Shadow position:</strong> {typeof position?.shadow === "boolean" ? formatBoolean(position.shadow) : "—"}
                </Typography>
                <Typography variant="body2">
                  <strong>On demand:</strong> {typeof position?.onDemand === "boolean" ? formatBoolean(position.onDemand) : "—"}
                </Typography>
              </Box>
            </Stack>
          </Paper>
        </Popper>
      </Box>
    </ClickAwayListener>
  );
}
